import React, { useEffect, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
  fetchWisudaPendaftaran,
  postWisudaPendaftaran,
  postWisudaDokumen,
  getWisudaDokumen,
} from "../../api";
import { saToast } from "../../helpers";
import { titleState } from "../../storages";
import { tokenState, userState } from "../../storages/auth";
import Loading from "../../components/Loading";
import LoginAs from "../../components/layouts/LoginAs";

const Wisuda = () => {
  const setTitle = useSetRecoilState(titleState);

  const token = useRecoilValue(tokenState);
  const user = useRecoilValue(userState);

  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(null);

  const [showDocument, setShowDocument] = useState(false);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [documentMimeType, setDocumentMimeType] = useState(null);
  const [documentLoading, setDocumentLoading] = useState(false);

  useEffect(() => {
    setTitle("Pendaftaran Wisuda");
  }, [setTitle]);

  // =========================
  // FORMAT TANGGAL
  // =========================
  const formatWisudaDate = (date) => {
    if (!date) return "-";

    const d = new Date(date);

    if (isNaN(d.getTime())) return "-";

    return d.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  // =========================
  // FETCH DATA
  // =========================
  const fetchData = async () => {
    try {
      setIsLoaded(false);

      const res = await fetchWisudaPendaftaran(token);

      if (res.data) {
        setData(res.data);
      }
    } catch (err) {
      console.log(err);

      saToast(
        "error",
        err?.response?.data?.message ||
          "Gagal mengambil data pendaftaran wisuda."
      );
    } finally {
      setIsLoaded(true);
    }
  };

  useEffect(() => {
    if (token && user?.id) {
      fetchData();
    }
  }, [token, user?.id]);

  // =========================
  // DAFTAR WISUDA
  // =========================
  const handleDaftar = async () => {
    try {
      const res = await postWisudaPendaftaran(token);

      if (res.data) {
        saToast("success", "Pendaftaran wisuda berhasil.");

        await fetchData();
      }
    } catch (err) {
      console.log(err);

      saToast(
        "error",
        err?.response?.data?.message ||
          "Gagal melakukan pendaftaran wisuda."
      );
    }
  };

  // =========================
  // UPLOAD DOKUMEN
  // =========================
  const handleUpload = async (persyaratan, file) => {
    if (!file) {
      return;
    }

    const registration =
      data?.registration || data?.pendaftaran;

    if (!registration?.id) {
      saToast("error", "Data pendaftaran tidak ditemukan.");
      return;
    }

    // Validasi ukuran file
    if (
      persyaratan.max_file_size &&
      file.size > Number(persyaratan.max_file_size)
    ) {
      const maxMB =
        Number(persyaratan.max_file_size) / (1024 * 1024);

      saToast(
        "error",
        `Ukuran file maksimal ${maxMB} MB.`
      );

      return;
    }

    // Validasi MIME type
    if (persyaratan.allowed_mime_types) {
      const allowedTypes = persyaratan.allowed_mime_types
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

      if (
        allowedTypes.length > 0 &&
        !allowedTypes.includes(file.type)
      ) {
        saToast(
          "error",
          "Format file tidak diperbolehkan."
        );

        return;
      }
    }

    try {
      setUploading(persyaratan.id);

      await postWisudaDokumen(
        registration.id,
        persyaratan.id,
        file,
        token
      );

      saToast(
        "success",
        "Dokumen berhasil diupload dan menunggu verifikasi."
      );

      await fetchData();
    } catch (err) {
      console.log(err);

      saToast(
        "error",
        err?.response?.data?.message ||
          "Gagal mengupload dokumen."
      );
    } finally {
      setUploading(null);
    }
  };

  // =========================
  // LIHAT DOKUMEN
  // =========================
  const handleViewDocument = async (dokumenId) => {
    try {
      setDocumentLoading(true);
      setShowDocument(true);

      const res = await getWisudaDokumen(
        dokumenId,
        token
      );

      const mimeType =
        res.headers["content-type"] ||
        "application/pdf";

      const blob = new Blob([res.data], {
        type: mimeType,
      });

      const url = window.URL.createObjectURL(blob);

      setDocumentUrl(url);
      setDocumentMimeType(mimeType);
    } catch (err) {
      console.log("Error dokumen:", err);
      console.log("Status:", err?.response?.status);
      console.log("Response:", err?.response?.data);

      setShowDocument(false);

      saToast(
        "error",
        `Dokumen tidak dapat dibuka${
          err?.response?.status
            ? ` (${err.response.status})`
            : ""
        }`
      );
    } finally {
      setDocumentLoading(false);
    }
  };

  // =========================
  // TUTUP MODAL DOKUMEN
  // =========================
  const handleCloseDocument = () => {
    if (documentUrl) {
      window.URL.revokeObjectURL(documentUrl);
    }

    setDocumentUrl(null);
    setDocumentMimeType(null);
    setShowDocument(false);
  };

  // =========================
  // FORMAT STATUS
  // =========================
  const getStatusLabel = (status) => {
    switch (status) {
      case "draft":
        return "Draft";

      case "submitted":
        return "Menunggu Verifikasi";

      case "revision":
        return "Perlu Perbaikan";

      case "registered":
        return "Terdaftar";

      case "cancelled":
        return "Dibatalkan";

      case "belum_daftar":
        return "Belum Daftar";

      case "belum_upload":
        return "Belum Upload";

      case "pending":
        return "Menunggu Verifikasi";

      case "approved":
        return "Disetujui";

      case "rejected":
        return "Ditolak";

      default:
        return status || "-";
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "registered":
      case "approved":
        return "badge bg-success";

      case "rejected":
      case "revision":
        return "badge bg-danger";

      case "pending":
      case "submitted":
        return "badge bg-warning text-dark";

      case "belum_upload":
        return "badge bg-secondary";

      default:
        return "badge bg-secondary";
    }
  };

  // =========================
  // LOADING
  // =========================
  if (!isLoaded) {
    return <Loading />;
  }

  const periode = data?.period || data?.periode;

  const registration =
    data?.registration || data?.pendaftaran;

  const requirements =
    data?.requirements ||
    data?.persyaratan ||
    [];

  // ==========================================================
  // PERIODE ADA TETAPI MAHASISWA TIDAK ELIGIBLE
  // ==========================================================
  if (data?.eligible === false && periode) {
    return (
      <>
        <div className="bg-white shadow rounded-3 mx-2 px-1 pt-3">
          <LoginAs />

          <div className="row">
            <div className="col-lg-12 mt-2 px-2">

              {/* HEADER */}
              <div className="bg-gradient-primary rounded-3 shadow-nice p-4 mb-4 text-center">
                <h4 className="text-white mb-1 fw-semibold">
                  Form Pendaftaran Wisuda
                </h4>
              </div>

              {/* INFO PERIODE */}
              <div className="card border-0 shadow-sm rounded-3 mb-4">
                <div className="card-body">
                  <h5 className="fw-bold mb-3">
                    {periode.nama}
                  </h5>

                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <small className="text-muted">
                        Kode Periode
                      </small>

                      <div className="fw-semibold">
                        {periode.kode}
                      </div>
                    </div>

                    <div className="col-md-6 mb-2">
                      <small className="text-muted">
                        Tanggal Wisuda
                      </small>

                      <div className="fw-semibold">
                        {formatWisudaDate(
                          periode.tanggal_wisuda
                        )}
                      </div>
                    </div>

                    <div className="col-md-6 mb-2">
                      <small className="text-muted">
                        Mulai Pendaftaran
                      </small>

                      <div className="fw-semibold">
                        {formatWisudaDate(
                          periode.tanggal_mulai_pendaftaran
                        )}
                      </div>
                    </div>

                    <div className="col-md-6 mb-2">
                      <small className="text-muted">
                        Selesai Pendaftaran
                      </small>

                      <div className="fw-semibold">
                        {formatWisudaDate(
                          periode.tanggal_selesai_pendaftaran
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* TIDAK ELIGIBLE */}
              <div className="alert alert-warning rounded-3 text-center py-5">
                <h5 className="fw-bold mb-2">
                  Anda Belum Dapat Mendaftar
                </h5>

                <p className="mb-0">
                  {data.message ||
                    "Anda tidak termasuk peserta yang dapat mendaftar pada periode wisuda ini."}
                </p>
              </div>

            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="bg-white shadow rounded-3 mx-2 px-1 pt-3">
        <LoginAs />

        <div className="row">
          <div className="col-lg-12 mt-2 px-2">

            {/* HEADER */}
            <div className="bg-gradient-primary rounded-3 shadow-nice p-4 mb-4 text-center">
              <h4 className="text-white mb-1 fw-semibold">
                Form Pendaftaran Wisuda
              </h4>
            </div>

            {/* TIDAK ADA PERIODE */}
            {!periode && (
              <div className="alert alert-warning rounded-3 text-center py-5">
                <h5 className="fw-bold mb-2">
                  Pendaftaran Wisuda Belum Dibuka
                </h5>

                <p className="mb-0">
                  Saat ini belum terdapat periode pendaftaran wisuda
                  yang dibuka.
                </p>
              </div>
            )}

            {/* ADA PERIODE */}
            {periode && (
              <>
                {/* INFO PERIODE */}
                <div className="card border-0 shadow-sm rounded-3 mb-4">
                  <div className="card-body">
                    <h5 className="fw-bold mb-3">
                      {periode.nama}
                    </h5>

                    <div className="row">
                      {/* <div className="col-md-6 mb-2">
                        <small className="text-muted">
                          Kode Periode
                        </small>

                        <div className="fw-semibold">
                          {periode.kode}
                        </div>
                      </div>

                      <div className="col-md-6 mb-2">
                        <small className="text-muted">
                          Tanggal Wisuda
                        </small>

                        <div className="fw-semibold">
                          {formatWisudaDate(
                            periode.tanggal_wisuda
                          )}
                        </div>
                      </div> */}

                      <div className="col-md-6 mb-2">
                        <small className="text-muted">
                          Mulai Pendaftaran
                        </small>

                        <div className="fw-semibold">
                          {formatWisudaDate(
                            periode.tanggal_mulai_pendaftaran
                          )}
                        </div>
                      </div>

                      <div className="col-md-6 mb-2">
                        <small className="text-muted">
                          Pendaftaran Ditutup
                        </small>

                        <div className="fw-semibold">
                          {formatWisudaDate(
                            periode.tanggal_selesai_pendaftaran
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* BELUM DAFTAR */}
                {!registration && (
                  <div className="card border-0 shadow-sm rounded-3 mb-4">
                    <div className="card-body text-center py-5">

                      <h5 className="fw-bold">
                        Anda Belum Terdaftar
                      </h5>

                      <p className="text-muted">
                        Silakan melakukan pendaftaran wisuda
                        untuk periode ini.
                      </p>

                      <button
                        type="button"
                        className="btn btn-primary rounded-pill px-4"
                        onClick={handleDaftar}
                      >
                        Daftar Wisuda
                      </button>

                    </div>
                  </div>
                )}

                {/* SUDAH DAFTAR */}
                {registration && (
                  <>
                    {/* INFO PENDAFTARAN */}
                    <div className="card border-0 shadow-sm rounded-3 mb-4">
                      <div className="card-body">

                        <h5 className="fw-bold mb-3">
                          Data Pendaftaran
                        </h5>

                        <div className="row">

                          <div className="col-md-6 mb-3">
                            <small className="text-muted">
                              Nomor Pendaftaran
                            </small>

                            <div className="fw-bold">
                              {registration.nomor_pendaftaran}
                            </div>
                          </div>

                          <div className="col-md-6 mb-3">
                            <small className="text-muted">
                              Status
                            </small>

                            <div>
                              <span
                                className={getStatusClass(
                                  registration.status
                                )}
                              >
                                {getStatusLabel(
                                  registration.status
                                )}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <small className="text-muted">
                              Tanggal Daftar
                            </small>

                            <div>
                              {formatWisudaDate(
                                registration.tanggal_daftar
                              )}
                            </div>
                          </div>

                          {registration.catatan && (
                            <div className="col-md-12 mt-3">
                              <div className="alert alert-warning mb-0">

                                <strong>
                                  Catatan:
                                </strong>

                                <div className="mt-1">
                                  {registration.catatan}
                                </div>

                              </div>
                            </div>
                          )}

                        </div>
                      </div>
                    </div>

                    {/* PERSYARATAN */}
                    <div className="card border-0 shadow-sm rounded-3 mb-5">
                      <div className="card-body">

                        <h5 className="fw-bold mb-4">
                          Persyaratan Wisuda
                        </h5>

                        {requirements.map(
                          (persyaratan, index) => {

                            const dokumen =
                              persyaratan.dokumen || null;

                            const approval =
                              persyaratan.approval || null;

                            const status =
                              persyaratan.status ||
                              approval?.status ||
                              (
                                persyaratan.perlu_upload &&
                                !dokumen
                                  ? "belum_upload"
                                  : "pending"
                              );

                            return (
                              <div
                                key={persyaratan.id}
                                className="border rounded-3 p-3 mb-3"
                              >

                                <div className="row align-items-center">

                                  {/* NOMOR */}
                                  <div className="col-md-1 text-center mb-2 mb-md-0">
                                    <div
                                      className="rounded-circle bg-light d-flex align-items-center justify-content-center mx-auto"
                                      style={{
                                        width: "38px",
                                        height: "38px",
                                      }}
                                    >
                                      <strong>
                                        {index + 1}
                                      </strong>
                                    </div>
                                  </div>

                                  {/* INFORMASI */}
                                  <div className="col-md-5 mb-2 mb-md-0">

                                    <div className="fw-semibold">
                                      {persyaratan.nama}
                                    </div>

                                    {persyaratan.deskripsi && (
                                      <small className="text-muted">
                                        {persyaratan.deskripsi}
                                      </small>
                                    )}

                                    {persyaratan.wajib && (
                                      <div>
                                        <small className="text-danger">
                                          * Wajib
                                        </small>
                                      </div>
                                    )}

                                  </div>

                                  {/* STATUS */}
                                  <div className="col-md-2 mb-2 mb-md-0">

                                    <small className="text-muted d-block">
                                      Status
                                    </small>

                                    <span
                                      className={getStatusClass(
                                        status
                                      )}
                                    >
                                      {getStatusLabel(
                                        status
                                      )}
                                    </span>

                                  </div>

                                  {/* DOKUMEN / UPLOAD */}
                                  <div className="col-md-4">

                                    {dokumen && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary rounded-pill me-2 mb-2"
                                        onClick={() =>
                                          handleViewDocument(
                                            dokumen.id
                                          )
                                        }
                                      >
                                        Lihat Dokumen
                                      </button>
                                    )}

                                    {persyaratan.perlu_upload &&
                                      status !== "approved" && (
                                        <>

                                          <input
                                            type="file"
                                            id={`file-${persyaratan.id}`}
                                            className="d-none"
                                            accept={
                                              persyaratan.allowed_mime_types
                                                ? persyaratan.allowed_mime_types.replaceAll(
                                                    " ",
                                                    ""
                                                  )
                                                : undefined
                                            }
                                            onChange={(e) => {
                                              const file =
                                                e.target.files?.[0];

                                              if (file) {
                                                handleUpload(
                                                  persyaratan,
                                                  file
                                                );
                                              }

                                              e.target.value = "";
                                            }}
                                          />

                                          <label
                                            htmlFor={`file-${persyaratan.id}`}
                                            className={`btn btn-sm ${
                                              status === "rejected"
                                                ? "btn-danger"
                                                : "btn-primary"
                                            } rounded-pill mb-2`}
                                          >
                                            {uploading ===
                                            persyaratan.id
                                              ? "Mengupload..."
                                              : status === "rejected"
                                              ? "Upload Ulang"
                                              : dokumen
                                              ? "Ganti Dokumen"
                                              : "Upload Dokumen"}
                                          </label>

                                        </>
                                      )}

                                    {status === "rejected" &&
                                      approval?.catatan && (
                                        <div className="alert alert-danger mt-2 mb-0 small">

                                          <strong>
                                            Catatan:
                                          </strong>

                                          <div>
                                            {approval.catatan}
                                          </div>

                                        </div>
                                      )}

                                  </div>

                                </div>

                              </div>
                            );
                          }
                        )}

                      </div>
                    </div>
                  </>
                )}

              </>
            )}

          </div>
        </div>
      </div>

      {/* MODAL LIHAT DOKUMEN */}
      {showDocument && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          }}
          tabIndex="-1"
        >

          <div className="modal-dialog modal-xl modal-dialog-centered">

            <div className="modal-content rounded-3 overflow-hidden">

              {/* HEADER */}
              <div className="modal-header bg-gradient-primary">

                <h5 className="modal-title text-white">
                  Lihat Dokumen
                </h5>

                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={handleCloseDocument}
                />

              </div>

              {/* BODY */}
              <div
                className="modal-body p-0"
                style={{
                  minHeight: "70vh",
                }}
              >

                {documentLoading && (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{
                      minHeight: "70vh",
                    }}
                  >
                    <Loading />
                  </div>
                )}

                {!documentLoading && documentUrl && (
                  <>
                    {documentMimeType === "application/pdf" ? (
                      <iframe
                        src={documentUrl}
                        title="Dokumen Wisuda"
                        width="100%"
                        style={{
                          height: "75vh",
                          border: "none",
                        }}
                      />
                    ) : documentMimeType?.startsWith("image/") ? (
                      <div
                        className="d-flex justify-content-center align-items-center p-3"
                        style={{
                          minHeight: "70vh",
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        <img
                          src={documentUrl}
                          alt="Dokumen Wisuda"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "70vh",
                            objectFit: "contain",
                          }}
                          className="rounded shadow-sm"
                        />
                      </div>
                    ) : (
                      <div className="text-center p-5">
                        <p className="mb-0">
                          Format dokumen tidak dapat ditampilkan
                          langsung.
                        </p>
                      </div>
                    )}
                  </>
                )}

              </div>

              {/* FOOTER */}
              <div className="modal-footer">

                <button
                  type="button"
                  className="btn btn-secondary rounded-pill px-4"
                  onClick={handleCloseDocument}
                >
                  Tutup
                </button>

              </div>

            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default Wisuda;